import type {
  ColorRule,
  ColorScale,
  SemanticColor,
  SeriesColorConfig,
} from './chart-types';
import type { ChartConfig } from '@/hen-ui/shadcn/chart';
import { getDivergingPalette } from './chart-palette';
import { warnOnce } from '@/hen-ui/utils';

const SEMANTIC_COLOR_TOKENS: Record<SemanticColor, string> = {
  positive: 'var(--success-500, #0D7D4D)',
  // `negative` and `error` are intentional visual aliases — both resolve
  // to the same `--error-500` token. The string keys are distinct so
  // rule dedup and labels can differentiate them, but in the legend two
  // entries with these colors will look identical. Author rules with
  // only one of them per series unless distinct labels are desired.
  negative: 'var(--error-500, #D23228)',
  neutral: 'var(--neutral-500, #434953)',
  warning: 'var(--warning-500, #D6B600)',
  error: 'var(--error-500, #D23228)',
  info: 'var(--info-500, #00688D)',
};

const VALID_SEMANTIC_COLORS = new Set<string>(
  Object.keys(SEMANTIC_COLOR_TOKENS),
);

const EXPRESSION_RE = /^(value)\s*(===?|!==?|>=?|<=?)\s*(-?\d+\.?\d*)$/;

export type ParsedExpression =
  | { op: string; comparand: number }
  | 'always'
  | null;

/**
 * Parse a `ColorRule.when` expression into a comparison + comparand,
 * the literal `'always'` for unconditional rules, or `null` for invalid
 * input.
 *
 * Accepted syntax:
 * - `'true'` — always matches (returns `'always'`).
 * - `value <op> <number>` where `<op>` is one of `==`, `===`, `!=`,
 *   `!==`, `>`, `>=`, `<`, `<=`. Whitespace around the operator is
 *   ignored. `==` / `!=` are normalized to `===` / `!==` so authors
 *   can write either form.
 *
 * The left-hand side is always the literal token `value` — there is no
 * support for arbitrary expressions, function calls, or referencing
 * other data-row fields. This keeps rule evaluation cheap enough to run
 * once per data point per render.
 */
export function parseWhenExpression(when: string): ParsedExpression {
  const trimmed = when.trim();
  if (trimmed === 'true') return 'always';

  const match = EXPRESSION_RE.exec(trimmed);
  if (!match) return null;

  const op = match[2] === '==' ? '===' : match[2] === '!=' ? '!==' : match[2];
  const comparand = Number(match[3]);
  return { op, comparand };
}

function evaluateOp(value: number, op: string, comparand: number): boolean {
  switch (op) {
    case '===':
      return value === comparand;
    case '!==':
      return value !== comparand;
    case '>':
      return value > comparand;
    case '>=':
      return value >= comparand;
    case '<':
      return value < comparand;
    case '<=':
      return value <= comparand;
    default:
      return false;
  }
}

/**
 * Resolve a `SemanticColor` token to its CSS custom property expression.
 * Returns `undefined` for unknown tokens.
 */
export function resolveSemanticColorToken(
  color: SemanticColor,
): string | undefined {
  return SEMANTIC_COLOR_TOKENS[color];
}

/**
 * Evaluate color rules for a single data point value and return the
 * resolved CSS color string.
 *
 * Evaluation order:
 * 1. `colorRules` — first matching rule's color is returned.
 * 2. `semanticColor` — applied when no rule matches (or no rules defined).
 *    `'auto-by-value'` maps value >= 0 → positive, value < 0 → negative.
 * 3. `defaultColor` — fallback when nothing else applies.
 */
export function evaluateColorRules(
  rules: ColorRule[] | undefined,
  value: number | null,
  semanticColor: SeriesColorConfig['semanticColor'],
  defaultColor: string,
): string {
  // `value !== null` narrows the TS type to `number`, but does NOT reject
  // `NaN` / `±Infinity` — those can leak in from upstream divide-by-zero
  // or malformed data rows. `Number.isFinite` guards both checks.
  if (rules && rules.length > 0 && value !== null && Number.isFinite(value)) {
    // First-in-array wins. In dev we keep scanning after the first match
    // to detect ambiguity (multiple rules that would match the same
    // value) so authors get a hint that later rules are unreachable for
    // overlapping points. In production we break on first match to avoid
    // the extra work on the hot per-cell render path.
    let matchedIndex = -1;
    let matchedColor: SemanticColor | undefined;

    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      if (!VALID_SEMANTIC_COLORS.has(rule.color)) {
        if (process.env.NODE_ENV !== 'production') {
          // De-duped: per-cell render path runs this on every data point
          // on every re-render; without `warnOnce` a chart with `N` points
          // and one invalid rule emits `N` warnings per render in dev.
          warnOnce(
            `chart-color-rules/invalid-color:${rule.color}`,
            `[hen-ui/chart-color-rules] Unknown semantic color "${rule.color}" — rule skipped.`,
          );
        }
        continue;
      }

      const parsed = parseWhenExpression(rule.when);
      if (parsed === null) {
        if (process.env.NODE_ENV !== 'production') {
          warnOnce(
            `chart-color-rules/invalid-when:${rule.when}`,
            `[hen-ui/chart-color-rules] Invalid expression "${rule.when}" — rule skipped.`,
          );
        }
        continue;
      }

      const isMatch =
        parsed === 'always' || evaluateOp(value, parsed.op, parsed.comparand);
      if (!isMatch) continue;

      if (matchedIndex === -1) {
        matchedIndex = i;
        matchedColor = rule.color;
        if (process.env.NODE_ENV === 'production') break;
      } else {
        // A later rule also matches this value — first-in-array still
        // wins, but emit a dev-only hint so authors notice that the
        // later rule is unreachable whenever both conditions overlap.
        // Keyed by the (winning, losing) `when` pair so unique conflicts
        // surface once across the lifetime of the module.
        warnOnce(
          `chart-color-rules/ambiguous:${rules[matchedIndex].when}=>${rule.when}`,
          `[hen-ui/chart-color-rules] Rule "${rule.when}" overlaps with earlier rule "${rules[matchedIndex].when}" — first match wins; the later rule will not be applied to overlapping values.`,
        );
      }
    }

    if (matchedColor !== undefined) {
      return SEMANTIC_COLOR_TOKENS[matchedColor];
    }
  }

  if (semanticColor) {
    if (semanticColor === 'auto-by-value') {
      if (value !== null && Number.isFinite(value)) {
        return value >= 0
          ? SEMANTIC_COLOR_TOKENS.positive
          : SEMANTIC_COLOR_TOKENS.negative;
      }
      return defaultColor;
    }

    if (VALID_SEMANTIC_COLORS.has(semanticColor)) {
      return SEMANTIC_COLOR_TOKENS[semanticColor as SemanticColor];
    }
  }

  return defaultColor;
}

/**
 * Resolve the *base* color a series should render with — the color that
 * recharts will read off the `<Bar fill>` / `<Line stroke>` prop and bake
 * into its legend payload (and therefore the legend swatch).
 *
 * Returns the resolved semantic-color CSS token when the series declares a
 * `semanticColor` (other than `'auto-by-value'`, which has no single
 * representative color), and the standard `var(--color-${key})` CSS
 * variable otherwise.
 *
 * Per-point `colorRules` are still applied on top via `<Cell>` / custom dot
 * renderers — this function only governs the series-level default.
 */
export function resolveSeriesBaseColor(
  key: string,
  seriesColorConfigs: Record<string, SeriesColorConfig> | undefined,
): string {
  const cfg = seriesColorConfigs?.[key];
  const semantic = cfg?.semanticColor;
  if (
    semantic &&
    semantic !== 'auto-by-value' &&
    VALID_SEMANTIC_COLORS.has(semantic)
  ) {
    return SEMANTIC_COLOR_TOKENS[semantic as SemanticColor];
  }
  return `var(--color-${key})`;
}

/**
 * Default human-readable labels for each `SemanticColor` token, used to
 * compose per-color legend entry labels (e.g. "Revenue (Negative)").
 */
const SEMANTIC_COLOR_LABELS: Record<SemanticColor, string> = {
  positive: 'Positive',
  negative: 'Negative',
  neutral: 'Neutral',
  warning: 'Warning',
  error: 'Error',
  info: 'Info',
};

/**
 * Compose a legend label for a per-color sub-entry, e.g.
 * `"Revenue (Negative)"`. Suppresses the parenthetical when the series
 * label already matches the semantic label (case-insensitive) so a
 * series literally named "Positive" doesn't render as "Positive
 * (Positive)" — common in WaterfallChart where the two series are
 * already named after their sign.
 */
function composeSemanticLegendLabel(
  seriesLabel: string,
  semantic: SemanticColor,
): string {
  const semanticLabel = SEMANTIC_COLOR_LABELS[semantic];
  if (seriesLabel.trim().toLowerCase() === semanticLabel.toLowerCase()) {
    return seriesLabel;
  }
  return `${seriesLabel} (${semanticLabel})`;
}

/**
 * Compose a legend label for a diverging-scale arm. The high arm colors
 * values `≥ 0`, the low arm values `< 0` (mirrors
 * {@link resolveDivergingArmColor}'s zero anchor), so the suffix is a sign
 * range rather than a semantic word — the arm colors are the palette
 * blue/orange (etc.), NOT the green/red success/error tokens, and reusing
 * "Positive/Negative" here would imply the wrong meaning.
 *
 * When `signPure` is true the series is known to render only one arm (e.g.
 * WaterfallChart's sign-pure positive/negative series), so the series label
 * already conveys the sign and the suffix is dropped to avoid redundancy
 * (`"Increase (≥ 0)"`).
 */
function composeDivergingArmLabel(
  seriesLabel: string,
  arm: 'low' | 'high',
  signPure: boolean,
): string {
  if (signPure) return seriesLabel;
  return `${seriesLabel} (${arm === 'high' ? '≥ 0' : '< 0'})`;
}

/**
 * Single entry in the expanded legend payload. Each series produces 1..N
 * entries depending on how many distinct semantic colors it can render
 * at runtime (base color + one per unique `colorRules[].color`, OR
 * `[positive, negative]` for `'auto-by-value'`).
 */
export type LegendEntry = {
  /** Unique React key — e.g. `"revenue:base"` or `"revenue:rule:negative"`. */
  id: string;
  /**
   * Underlying series key. Multiple `LegendEntry` rows can share the
   * same `seriesKey` (one base + one per rule color). Used for the
   * click-to-toggle handler so toggling any entry hides/shows the entire
   * series.
   */
  seriesKey: string;
  /** Display label (e.g. `"Revenue"` or `"Revenue (Negative)"`). */
  label: string;
  /** CSS color expression for the swatch / Recharts icon. */
  color: string;
};

/**
 * Describes an active diverging color scale so {@link expandLegendEntries}
 * can render arm-aware legend entries (low `< 0` / high `≥ 0`) that match
 * the bi-color, sign-anchored bars/dots — instead of a single swatch that
 * would only show the high arm.
 */
export interface LegendDivergingOptions {
  /** The chart's active color scale; arm logic only applies for `'diverging'`. */
  colorScale?: ColorScale | string | null;
  /** Diverging family name; resolved via {@link getDivergingPalette}. */
  divergingPalette?: string | null;
  /**
   * Series known to render only one arm (sign-pure series, e.g.
   * WaterfallChart's positive/negative deltas). Maps the series key to the
   * single arm it renders so the legend shows one swatch (with the series'
   * own label) instead of both arms.
   */
  armByKey?: Record<string, 'low' | 'high'>;
}

/**
 * Expand series into a per-(series × color) legend payload so the legend
 * reflects every color the chart can render on-chart.
 *
 * Behavior per series:
 * - **No `seriesColorConfigs` for the key, or no `semanticColor` + no
 *   `colorRules`** — one entry: `[{ label: "Revenue", color: base }]`.
 * - **`semanticColor: 'positive'` only** — one entry with the resolved
 *   semantic token as color (the base entry).
 * - **`semanticColor: 'positive'` + `colorRules: [{when, color: 'negative'}]`**
 *   — two entries: base `Revenue` (green) and rule `Revenue (Negative)` (red).
 *   Rule colors are deduped — listing the same color twice in `colorRules`
 *   does not double the legend entry.
 * - **`semanticColor: 'auto-by-value'`** — two entries:
 *   `Revenue (Positive)` and `Revenue (Negative)`, NO standalone base
 *   (the series has no single representative color).
 *
 * The base entry is suppressed when its color would duplicate a rule
 * entry's color (e.g. `semanticColor: 'negative'` + a rule that also
 * resolves to `'negative'`) — avoids two identical swatches with
 * confusingly different labels.
 *
 * **Known limitation**: dedup only fires when `semanticColor` is set —
 * a series with no `semanticColor` + a rule that happens to resolve to
 * the same color as the palette `var(--color-${key})` will render two
 * visually identical swatches. The palette CSS var is opaque, so we
 * can't statically compare it against semantic tokens. Acceptable in
 * practice because authors who reach for `colorRules` almost always
 * also declare a `semanticColor` to anchor the rest-state color.
 *
 * Returns a stable order: for each series in `series`, base first, then
 * rule entries in declaration order.
 */
export function expandLegendEntries(
  series: readonly string[],
  config: ChartConfig | undefined,
  seriesColorConfigs: Record<string, SeriesColorConfig> | undefined,
  divergingOptions?: LegendDivergingOptions,
): LegendEntry[] {
  const isDiverging = divergingOptions?.colorScale === 'diverging';
  const entries: LegendEntry[] = [];

  // Build the deduped + validated per-series rule-color entries. Shared by
  // the diverging-arm branch and the default branch so both stay in lockstep
  // with `evaluateColorRules`' validation.
  const collectRuleEntries = (
    key: string,
    label: string,
    cfg: SeriesColorConfig | undefined,
  ): { ruleEntries: LegendEntry[]; seenRuleColors: Set<string> } => {
    const ruleEntries: LegendEntry[] = [];
    const seenRuleColors = new Set<string>();
    if (!cfg?.colorRules) return { ruleEntries, seenRuleColors };
    for (const rule of cfg.colorRules) {
      // Mirror `evaluateColorRules`'s validation: a rule with an unknown
      // color OR an unparseable `when` expression never paints a cell at
      // runtime, so it must not appear in the legend either (otherwise the
      // legend advertises a color the chart can never render).
      if (!VALID_SEMANTIC_COLORS.has(rule.color)) continue;
      if (parseWhenExpression(rule.when) === null) continue;
      if (seenRuleColors.has(rule.color)) continue;
      seenRuleColors.add(rule.color);
      const semantic = rule.color as SemanticColor;
      ruleEntries.push({
        id: `${key}:rule:${semantic}`,
        seriesKey: key,
        label: composeSemanticLegendLabel(label, semantic),
        color: SEMANTIC_COLOR_TOKENS[semantic],
      });
    }
    return { ruleEntries, seenRuleColors };
  };

  for (const key of series) {
    const label = (config?.[key]?.label as string | undefined) ?? key;
    const cfg = seriesColorConfigs?.[key];

    // Diverging scale drives the base color whenever the series has no
    // explicit `semanticColor` (static OR `auto-by-value`). The scale colors
    // each value by its sign, so a single swatch can't represent it — emit
    // one entry per arm (high = `≥ 0`, low = `< 0`), or just the one arm the
    // series renders when it is sign-pure (e.g. WaterfallChart). Rule colors
    // still layer on top, exactly as they paint on-chart.
    if (isDiverging && cfg?.semanticColor == null) {
      const palette = getDivergingPalette(divergingOptions?.divergingPalette);
      const lowArm = palette[0];
      const highArm = palette[palette.length - 1];
      const forcedArm = divergingOptions?.armByKey?.[key];
      const signPure = forcedArm != null;
      const pushArm = (arm: 'low' | 'high'): void => {
        entries.push({
          id: `${key}:arm:${arm}`,
          seriesKey: key,
          label: composeDivergingArmLabel(label, arm, signPure),
          color: arm === 'high' ? highArm : lowArm,
        });
      };
      if (forcedArm) {
        pushArm(forcedArm);
      } else {
        pushArm('high');
        pushArm('low');
      }
      const { ruleEntries } = collectRuleEntries(key, label, cfg);
      entries.push(...ruleEntries);
      continue;
    }

    // No config or only-palette series — single entry with palette color.
    if (!cfg) {
      entries.push({
        id: `${key}:base`,
        seriesKey: key,
        label,
        color: `var(--color-${key})`,
      });
      continue;
    }

    // `auto-by-value` has no single representative color; emit a positive
    // + negative pair (no base).
    if (cfg.semanticColor === 'auto-by-value') {
      entries.push({
        id: `${key}:rule:positive`,
        seriesKey: key,
        label: composeSemanticLegendLabel(label, 'positive'),
        color: SEMANTIC_COLOR_TOKENS.positive,
      });
      entries.push({
        id: `${key}:rule:negative`,
        seriesKey: key,
        label: composeSemanticLegendLabel(label, 'negative'),
        color: SEMANTIC_COLOR_TOKENS.negative,
      });
      continue;
    }

    const { ruleEntries, seenRuleColors } = collectRuleEntries(key, label, cfg);

    const baseColor = resolveSeriesBaseColor(key, seriesColorConfigs);
    // Suppress base when its color is already covered by a rule entry
    // (avoids two identical swatches with different labels).
    const baseSemantic =
      cfg.semanticColor && VALID_SEMANTIC_COLORS.has(cfg.semanticColor)
        ? (cfg.semanticColor as SemanticColor)
        : undefined;
    const baseDuplicatesRule =
      baseSemantic !== undefined && seenRuleColors.has(baseSemantic);

    if (!baseDuplicatesRule) {
      entries.push({
        id: `${key}:base`,
        seriesKey: key,
        label,
        color: baseColor,
      });
    }
    entries.push(...ruleEntries);
  }
  return entries;
}

/**
 * Check whether any series in the config map has active color rules,
 * used to gate the more expensive per-point Cell rendering path.
 *
 * @deprecated Prefer the per-series check `!!cfg?.colorRules?.length`
 * inline at each Bar / Line render site. This helper over-triggers
 * because it also returns `true` for series with only `semanticColor`
 * set — which now flow through `resolveSeriesBaseColor` on the `<Bar
 * fill>` / `<Line stroke>` prop and don't need per-point `<Cell>`
 * allocations. Retained only for backward-compat with external callers;
 * not used internally as of EDUN-12042.
 */
export function hasActiveColorRules(
  configs: Record<string, SeriesColorConfig> | undefined,
  seriesKeys: readonly string[],
): boolean {
  if (!configs) return false;
  return seriesKeys.some((key) => {
    const cfg = configs[key];
    return (
      cfg !== undefined &&
      ((cfg.colorRules && cfg.colorRules.length > 0) ||
        (cfg.semanticColor !== undefined && cfg.semanticColor !== null))
    );
  });
}

/**
 * Build a per-item indicator-color resolver for `<ChartTooltipContent
 * getItemIndicatorColor>`. Resolves the same color that an on-chart
 * `<Bar>` cell / `<Line>` dot would render at this data point — so the
 * tooltip indicator dot stays in sync with the underlying mark color when
 * `seriesColorConfigs` flips per-point via `colorRules`.
 *
 * Returns `undefined` when the helper can't be applied (no configs, no
 * matching series, non-numeric value) so the default
 * `item.payload.fill ?? item.color` fallback path runs.
 */
export function buildTooltipIndicatorColorResolver(
  seriesColorConfigs: Record<string, SeriesColorConfig> | undefined,
):
  | ((item: {
      dataKey?: string | number | ((obj: unknown) => unknown);
      value?: unknown;
      color?: string;
    }) => string | undefined)
  | undefined {
  if (!seriesColorConfigs) return undefined;
  return (item) => {
    const key =
      typeof item.dataKey === 'string' || typeof item.dataKey === 'number'
        ? String(item.dataKey)
        : undefined;
    if (!key) return undefined;
    const cfg = seriesColorConfigs[key];
    if (!cfg) return undefined;
    const value = typeof item.value === 'number' ? item.value : null;
    const fallback = item.color ?? `var(--color-${key})`;
    return evaluateColorRules(
      cfg.colorRules,
      value,
      cfg.semanticColor,
      fallback,
    );
  };
}

/**
 * Returns a new `ChartConfig` whose `color` for each series is overridden
 * with the resolved `semanticColor` token (when present).
 *
 * Why: `<ChartLegend>` reads its swatch color from `config[key].color`, but
 * the semantic-color override happens at recharts render time via `<Cell>`
 * fills / line strokes — invisible to the legend. Without this helper the
 * legend keeps showing the base palette color while the chart renders in
 * the semantic token, which is visually confusing.
 *
 * Series that only define `colorRules` (no `semanticColor`) intentionally
 * keep the base palette color in the legend — their on-chart fills are
 * per-point and have no single representative color.
 *
 * Returns the original `config` reference when there's nothing to override
 * so memo-equality checks downstream stay stable.
 */
export function withSemanticLegendColors(
  config: ChartConfig,
  seriesColorConfigs: Record<string, SeriesColorConfig> | undefined,
  seriesKeys: readonly string[],
): ChartConfig {
  if (!seriesColorConfigs) return config;
  let next: ChartConfig | undefined;
  for (const key of seriesKeys) {
    const cfg = seriesColorConfigs[key];
    if (!cfg?.semanticColor || cfg.semanticColor === 'auto-by-value') continue;
    if (!VALID_SEMANTIC_COLORS.has(cfg.semanticColor)) continue;
    const token = SEMANTIC_COLOR_TOKENS[cfg.semanticColor as SemanticColor];
    const base = config[key];
    if (!base) continue;
    if (!next) next = { ...config };
    // Strip `theme` via destructuring (not `theme: undefined`) so the
    // result truly omits the key. The `ChartConfig` union demands EITHER
    // `{color?: string; theme?: never}` OR `{color?: never; theme: …}` —
    // `theme: undefined` in a spread is present-with-undefined, which
    // satisfies neither branch in strict downstream tsc configs (e.g.
    // hen-site's tsconfig.entrypoint.json). The `_theme` placeholder is
    // intentionally unused; the rest spread is what carries the future-
    // proof "preserve every other ChartConfig field (label, icon, …)"
    // behavior the original code aimed for.
    const { theme: _theme, ...baseWithoutTheme } = base;
    next[key] = { ...baseWithoutTheme, color: token };
  }
  return next ?? config;
}
